/**
 * Page Builder Wrapper
 * 
 * Higher-level wrapper component that provides team/organization context
 * and integrates with the SHARP application architecture.
 */

import React, { useState, useEffect } from 'react';
import { Box, Alert, CircularProgress, Typography, Breadcrumbs, Link } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrganization } from '../../context/OrganizationContext';
import { EnhancedPageEditor } from './EnhancedPageEditor';
import { PageDoc } from '../../types/page-builder.types';
import { ViewCategory } from '../../types/view.types';

interface PageBuilderWrapperProps {
  mode?: 'create' | 'edit' | 'view';
  teamId?: string;
  organizationId?: string;
  viewId?: string;
  onClose?: () => void;
  initialPageDoc?: PageDoc;
}

interface ContextData {
  organizationName?: string;
  teamName?: string;
  loading: boolean;
  error?: string;
}

export const PageBuilderWrapper: React.FC<PageBuilderWrapperProps> = ({
  mode = 'create',
  teamId,
  organizationId,
  viewId,
  onClose,
  initialPageDoc,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const { organizations, teams } = useOrganization();

  // Extract IDs from URL params if not provided as props
  const finalTeamId = teamId || params.teamId || '';
  const finalOrganizationId = organizationId || params.organizationId || '';
  const finalViewId = viewId || params.viewId;

  // Track the current viewId - needed for create mode where view is created on mount
  const [currentViewId, setCurrentViewId] = useState<string | undefined>(finalViewId);

  const [contextData, setContextData] = useState<ContextData>({
    loading: false,
  });

  useEffect(() => {
    loadContextData();
  }, [finalTeamId, finalOrganizationId, organizations, teams]);

  const loadContextData = () => {
    try {
      // Get the actual organization and team names from the context
      const organization = organizations.find(org => org.id === finalOrganizationId);
      const team = teams.find(t => t.id === finalTeamId);
      
      setContextData({
        organizationName: organization?.name,
        teamName: team?.name,
        loading: false,
      });
    } catch (error) {
      setContextData({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load context data',
      });
    }
  };

  const handleSaveSuccess = (savedViewId: string) => {
    // Update the tracked viewId when a view is created
    if (!currentViewId && savedViewId) {
      setCurrentViewId(savedViewId);

      // DON'T navigate - this would cause a route change and remount the component
      // Instead, just update the viewId state and let the component continue
      // The browser URL will stay at /views/create but that's okay
      // When user navigates away and comes back, they'll use /views/{id}/edit
    }
  };

  const handleError = (error: string) => {
    console.error('Page Builder Error:', error);
  };

  const getInitialMetadata = () => {
    if (mode === 'create') {
      return {
        title: 'Untitled View',
        description: 'A new view created with the page builder',
        category: 'custom' as ViewCategory,
        tags: [],
        isPublic: false,
        status: 'private' as const,
        teamId: finalTeamId,
        organizationId: finalOrganizationId,
      };
    }
    return undefined;
  };

  if (contextData.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading page builder...</Typography>
      </Box>
    );
  }

  if (contextData.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {contextData.error}
        </Alert>
      </Box>
    );
  }

  if (!finalTeamId || !finalOrganizationId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Team ID and Organization ID are required for the page builder.
        </Alert>
      </Box>
    );
  }

  return (
    <EnhancedPageEditor
      initialPageDoc={initialPageDoc}
      initialMetadata={getInitialMetadata()}
      viewId={currentViewId}
      teamId={finalTeamId}
      organizationId={finalOrganizationId}
      teamName={contextData.teamName}
      organizationName={contextData.organizationName}
      onSaveSuccess={handleSaveSuccess}
      onError={handleError}
      readonly={mode === 'view'}
    />
  );
};

export default PageBuilderWrapper;